// PE-6139 — Firebase anonymous session for guest PE2 calls (sign-up, forgot-username, password reset)
import { Injectable, inject } from '@angular/core';
import { getAuth, signInAnonymously, signOut, User } from 'firebase/auth';
import { StorageService } from './storage.service';

export const GUEST_PI_STORAGE_KEY = 'guest_pi';
export const GUEST_UID_STORAGE_KEY = 'guest_uid';

export interface GuestPiPayload {
  userid: string;
  token: string;
}

@Injectable({
  providedIn: 'root',
})
export class GuestFirebaseAuthService {
  private readonly storageService = inject(StorageService);
  // PE-6139 — one in-flight ensure so concurrent callers share a single anonymous sign-in
  private ensureInFlight: Promise<GuestPiPayload> | null = null;

  /**
   * PE-6139 — True when a portal session exists (signed-in user_info, pending MFA, or __session_id__).
   * Guest anonymous Bearer must never be created or attached in this state (authGuard routes).
   */
  hasPortalSession(): boolean {
    const userInfo = this.storageService.getSessionStorage('user_info');
    const pendingMfa = this.storageService.getSessionStorage('pending_mfa_login');
    const sessionId =
      userInfo?.sessionId ||
      pendingMfa?.userInfo?.sessionId ||
      sessionStorage.getItem('__session_id__');
    return !!String(sessionId ?? '').trim();
  }

  getGuestPi(): GuestPiPayload | null {
    const pi = this.storageService.getSessionStorage(GUEST_PI_STORAGE_KEY);
    if (!pi || typeof pi !== 'object') {
      return null;
    }
    const userid = String((pi as GuestPiPayload).userid ?? '').trim();
    const token = String((pi as GuestPiPayload).token ?? '').trim();
    if (!userid || !token) {
      return null;
    }
    return { userid, token };
  }

  getGuestToken(): string | null {
    return this.getGuestPi()?.token ?? null;
  }

  isTokenExpired(token: string | null | undefined): boolean {
    if (!token) {
      return true;
    }
    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) {
        return true;
      }
      const payload = JSON.parse(atob(payloadPart)) as { exp?: number };
      if (!payload.exp) {
        return true;
      }
      return Date.now() > payload.exp * 1000;
    } catch {
      return true;
    }
  }

  /**
   * Create or refresh anonymous Firebase session and persist token under guest_pi.
   * Reuses a valid session token across routes until expiry; concurrent calls share one promise.
   * No-ops creation after sign-in — throws if a portal session is already active.
   */
  async ensureAnonymousSession(): Promise<GuestPiPayload> {
    // PE-6139 — never mint anonymous tokens for signed-in / authGuard sessions
    if (this.hasPortalSession()) {
      throw new Error('Guest anonymous session is not available while signed in');
    }

    const existing = this.getGuestPi();
    if (existing && !this.isTokenExpired(existing.token)) {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser && currentUser.uid === existing.userid) {
        try {
          return await this.persistUserToken(currentUser, false);
        } catch {
          // fall through to shared create/refresh
        }
      } else if (!currentUser) {
        // PE-6139 — Firebase not restored yet; reuse stored token until it expires
        return existing;
      }
    }

    if (this.ensureInFlight) {
      return this.ensureInFlight;
    }

    this.ensureInFlight = this.createOrRefreshAnonymousSession().finally(() => {
      this.ensureInFlight = null;
    });
    return this.ensureInFlight;
  }

  async clearGuestSession(): Promise<void> {
    this.ensureInFlight = null;
    this.storageService.removeSessionStorageByKey(GUEST_PI_STORAGE_KEY);
    this.storageService.removeSessionStorageByKey(GUEST_UID_STORAGE_KEY);
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        await signOut(auth);
      }
    } catch {
      // Session keys already cleared; ignore Firebase sign-out failures
    }
  }

  private async createOrRefreshAnonymousSession(): Promise<GuestPiPayload> {
    const auth = getAuth();
    const existing = this.getGuestPi();
    const currentUser = auth.currentUser;

    if (currentUser && existing && !this.isTokenExpired(existing.token)) {
      try {
        return await this.persistUserToken(currentUser, false);
      } catch {
        // fall through to force refresh / new anonymous sign-in
      }
    }

    if (currentUser) {
      try {
        return await this.persistUserToken(currentUser, true);
      } catch {
        // fall through to new anonymous sign-in
      }
    }

    const userCredential = await signInAnonymously(auth);
    return this.persistUserToken(userCredential.user, false);
  }

  private async persistUserToken(user: User, forceRefresh: boolean): Promise<GuestPiPayload> {
    const accessToken = await user.getIdToken(forceRefresh);
    const payload: GuestPiPayload = { userid: user.uid, token: accessToken };
    this.storageService.setSessionStorage(GUEST_PI_STORAGE_KEY, payload);
    this.storageService.setSessionStorage(GUEST_UID_STORAGE_KEY, user.uid);
    return payload;
  }
}
